import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

export type ActivityDurationUnit = 'hours' | 'days' | 'months';

export interface ActivityRow {
  id: string;
  candidatureId?: string;
  status?: string;
  date?: Date;
  durationValue?: number;
  durationUnit?: ActivityDurationUnit;
  /** Compat legacy: ancienne donnée encore stockée en `hours`. */
  hours?: number;
  description?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

const COLLECTION = 'activities';
const DURATION_UNITS: ActivityDurationUnit[] = ['hours', 'days', 'months'];

function parseDurationUnit(value: unknown): ActivityDurationUnit {
  if (typeof value === 'string' && DURATION_UNITS.includes(value as ActivityDurationUnit)) {
    return value as ActivityDurationUnit;
  }
  return 'hours';
}

export async function listActivities(options?: {
  status?: string;
  limitCount?: number;
}): Promise<ActivityRow[]> {
  const clauses: any[] = [];
  if (options?.status) {
    clauses.push(where('status', '==', options.status));
  }
  clauses.push(orderBy('date', 'desc'));
  if (options?.limitCount) {
    clauses.push(limit(options.limitCount));
  }

  const snap = await getDocs(
    query(collection(db, COLLECTION), ...clauses)
  );

  return snap.docs.map((d) => {
    const data = d.data() as any;
    const legacyHours =
      typeof data.hours === 'number'
        ? data.hours
        : typeof data.hours === 'string'
          ? Number(data.hours)
          : undefined;
    const durationValue =
      typeof data.durationValue === 'number'
        ? data.durationValue
        : typeof data.durationValue === 'string'
          ? Number(data.durationValue)
          : legacyHours;
    const durationUnit = parseDurationUnit(data.durationUnit);
    return {
      id: d.id,
      candidatureId: data.candidatureId,
      status: data.status,
      date: data.date?.toDate ? data.date.toDate() : undefined,
      durationValue,
      durationUnit,
      hours: legacyHours,
      description: data.description,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
    };
  });
}

export async function updateActivityStatus(
  id: string,
  status: 'validated' | 'rejected' | 'pending'
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, { status });
}

export async function updateActivity(
  id: string,
  payload: {
    candidatureId: string;
    status: 'validated' | 'rejected' | 'pending';
    date: Date;
    durationValue: number;
    durationUnit: ActivityDurationUnit;
    description: string;
  }
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, {
    candidatureId: payload.candidatureId.trim(),
    status: payload.status,
    date: Timestamp.fromDate(payload.date),
    durationValue: payload.durationValue,
    durationUnit: payload.durationUnit,
    // Legacy mirror for existing dashboards/queries.
    hours: payload.durationUnit === 'hours' ? payload.durationValue : null,
    description: payload.description.trim() || null,
    updatedAt: new Date(),
  });
}

export async function createActivity(payload: {
  candidatureId: string;
  status: 'validated' | 'rejected' | 'pending';
  date: Date;
  durationValue: number;
  durationUnit: ActivityDurationUnit;
  description: string;
}): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    candidatureId: payload.candidatureId.trim(),
    status: payload.status,
    date: Timestamp.fromDate(payload.date),
    durationValue: payload.durationValue,
    durationUnit: payload.durationUnit,
    // Legacy mirror for existing dashboards/queries.
    hours: payload.durationUnit === 'hours' ? payload.durationValue : null,
    description: payload.description.trim() || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

